import { Modal, TextField } from 'melp-design/components';
import { useTranslation } from 'react-i18next';
import { ROUTES } from 'config';
import { usePredefinedToasts } from 'utils/Toast';
import { ModalProps } from 'store/modal';
import { FC } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form-latest';
import { routerHistory } from 'utils/router-history';
import { Stack } from '@mui/material';
import { useCopyBenefit } from 'store/benefits';
import { capitalizeFirstLetter } from 'utils/general';

interface Values {
  name: string;
}

interface Props extends ModalProps {
  benefitId: string;
  name: string;
}

export const CopyBenefitModal: FC<Props> = ({
  name,
  benefitId,
  closeModal,
}) => {
  const { t } = useTranslation();
  const predefinedToasts = usePredefinedToasts();

  const { control, handleSubmit } = useForm<Values>({
    defaultValues: {
      name: `(${capitalizeFirstLetter(t('common.copy'))}) ${name}`,
    },
  });

  const { mutate: copyBenefit, isLoading: isCopying } = useCopyBenefit();

  const onSubmit: SubmitHandler<Values> = async (values) => {
    copyBenefit(
      {
        benefitId,
        name: values.name,
      },
      {
        onSuccess: ({ data }) => {
          closeModal();
          predefinedToasts.success.added();
          routerHistory.push(ROUTES.benefits.details.replace(':id', data.id));
        },
      },
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Modal.Content
        title={t('actions.make_copy')}
        actions={[
          {
            variant: 'neutral-outline',
            onClick: () => closeModal(),
            label: t('common.cancel'),
          },
          {
            variant: 'primary',
            label: t('common.create'),
            type: 'submit',
            disabled: isCopying,
          },
        ]}
      >
        <Stack gap={2}>
          <Controller
            name="name"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <TextField
                label={t('surveys.systemName')}
                name={name}
                value={value}
                onChange={onChange}
                required
                ref={ref}
              />
            )}
          />
        </Stack>
      </Modal.Content>
    </form>
  );
};
